import { useCombobox, useMultipleSelection } from 'downshift';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';

const PageMultiSelect = ({
  onChange,
  isSingle = false,
  initialSelectedItems = [],
  pageType = ['checkout', 'event', 'form'],
}) => {
  const { sellerId, token } = useAuth();
  const [inputValue, setInputValue] = useState('');

  const { data = [], error } = useSWR(
    `/sellers/${sellerId}/pages?${stringify({
      search: inputValue,
      type: pageType,
      status: ['published', 'draft'],
      limit: 15,
    })}`,
    url => fetcher(url, token),
  );

  const items = data || [];

  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    setSelectedItems,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection({
    initialSelectedItems,
    itemToString: item => (item ? item._id : ''),
  });

  const getFilteredItems = () =>
    items.filter(
      item => selectedItems.findIndex(selectedItem => selectedItem._id === item._id) < 0,
    );

  const {
    isOpen,
    getToggleButtonProps,
    openMenu,
    closeMenu,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    inputValue,
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    items: getFilteredItems(),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep the menu open after selection.
          };
      }
      return changes;
    },
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue);
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setInputValue('');
            if (isSingle) {
              setSelectedItems([selectedItem]);
              closeMenu();
            } else {
              addSelectedItem(selectedItem);
            }
          }
          break;
        default:
          break;
      }
    },
  });

  useEffect(() => {
    if (onChange) {
      onChange(selectedItems);
    }
  }, [selectedItems]);

  return (
    <div>
      <div>
        {selectedItems.map((selectedItem, index) => {
          const { product } = selectedItem;

          return (
            <div
              className="flex justify-between mb-2"
              key={`selected-item-${index}`}
              {...getSelectedItemProps({ selectedItem, index })}
            >
              <div>
                <h5>{selectedItem.name}</h5>
                {selectedItem.type === 'checkout' && (
                  <div>
                    {getPriceLabel({
                      amount: product.price,
                      currency: product.currency,
                      interval: product.interval,
                      intervalCount: product.intervalCount,
                      type: product.type,
                    })}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  removeSelectedItem(selectedItem);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  width="16"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          );
        })}

        <div {...getComboboxProps()} className="mt-2 flex">
          <input
            className="flex-1 border border-gray-300 shadow-input focus:shadow-input-focus rounded-md mr-2 p-2 outline-none"
            placeholder="Type to search for a page..."
            {...getInputProps({
              getDropdownProps: getDropdownProps({ preventKeyAction: isOpen }),
              onFocus: () => {
                if (!isOpen) {
                  openMenu();
                }
              },
            })}
          />
          <button
            {...getToggleButtonProps()}
            aria-label={'toggle menu'}
            type="button"
            className="px-2"
          >
            &#8595;
          </button>
        </div>

        {error && <div>Something went wrong.</div>}
      </div>

      <ul {...getMenuProps()} className={`my-2 ${isOpen && 'border'} rounded`}>
        {isOpen &&
          getFilteredItems().map((item, index) => {
            const { product } = item;
            return (
              <li
                className="p-2 flex justify-between whitespace-nowrap gap-x-4"
                style={
                  highlightedIndex === index ? { backgroundColor: 'var(--gray-lightest)' } : {}
                }
                key={`${item}${index}`}
                {...getItemProps({ key: item._id, item, index })}
              >
                <span className="truncate">{item.name}</span>

                {item.type === 'checkout' && (
                  <div>
                    {getPriceLabel({
                      amount: product.price,
                      currency: product.currency,
                      interval: product.interval,
                      intervalCount: product.intervalCount,
                      type: product.type,
                    })}
                  </div>
                )}
              </li>
            );
          })}
      </ul>
    </div>
  );
};

export default PageMultiSelect;
